"use client";

import { useQuery } from "@tanstack/react-query";
import { NOTIFICATIONS_REFETCH_INTERVAL_MS } from "@/features/notifications/constants";
import { fetchAllMatchingNotifications } from "@/features/notifications/shared/fetchAllMatchingNotificationIds";
import type { NotificationFiltersState } from "@/features/notifications/shared/notificationFilters";
import { useAccessToken } from "@/hooks/shared/useAccessToken";
import { QueryKeys } from "@/lib/types";
import { useNotificationsRefetchInterval } from "./useNotificationsRefetchInterval";

interface UseNotificationsFullListParams {
  userId: number;
  filters: NotificationFiltersState;
  quickSearch: string;
  enabled?: boolean;
  refetchInterval?: number | false;
  activeRole?: string | null;
}

export function useNotificationsFullList({
  userId,
  filters,
  quickSearch,
  enabled = true,
  refetchInterval = NOTIFICATIONS_REFETCH_INTERVAL_MS,
  activeRole,
}: UseNotificationsFullListParams) {
  const accessToken = useAccessToken();
  const effectiveRefetchInterval =
    useNotificationsRefetchInterval(refetchInterval);

  return useQuery({
    queryKey: [
      QueryKeys.Notifications,
      "full-list",
      userId,
      filters,
      quickSearch,
      activeRole,
    ],
    queryFn: () =>
      fetchAllMatchingNotifications({
        accessToken: accessToken!,
        userId,
        filters,
        quickSearch,
        activeRole,
      }),
    enabled: !!accessToken && !!userId && enabled,
    refetchOnMount: false,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
    staleTime: 30 * 1000,
    refetchInterval: effectiveRefetchInterval,
    placeholderData: (previousData) => previousData,
  });
}
